import { useEffect, useState } from 'react';
import { Form, InputNumber, type FormInstance, Divider, Flex, Tag } from 'antd';
import { Space, Text, Button, DatePicker, InputText } from '@g2u/ui-kit';
import { format, isValid, parse } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { Big } from 'big.js';
import cx from 'classnames';

import env from 'env';

import { Condition, JsonDataItem, type JsonData, type WithOptionalDataTest } from 'types';
import styles from './legacy-services.module.scss';
import { encryptAesToken } from 'helpers/crypto';
import { JobServiceTypeLabel } from 'types';
import { useQuery } from '@tanstack/react-query';
import { getJobServiceSelection } from 'queries';
import { useParams } from 'react-router';
import { BookingStore } from 'store';
import { parseErrors, Toast } from 'helpers';

type Props = {
  job?: JsonDataItem;
  form: FormInstance;
  isRequired?: boolean;
  isLegacySource: boolean;
  totalPrice: string;
  onSelect: (products: JsonData | undefined) => void;
} & WithOptionalDataTest;

export enum Rate {
  SameDay = 'sds',
  Weekend = 'weekend',
  Cancellation = 'cancellation',
  Consultation = 'consultation',
}

export const LegacyServices: React.FC<Props> = observer((props) => {
  const { onSelect, isRequired, form, totalPrice, job, isLegacySource } = props;
  const [selectedProducts, setSelectedProducts] = useState<JsonData | undefined>();
  const hasSameDayService = job?.job.sameday_service === Condition.Yes;
  const hasWeekendService = job?.job.weekend_service === Condition.Yes;
  const hasCancelationFee = job?.job.cancelled === Condition.Yes;
  const newConsultation = {
    id: null,
    qty: 1,
    label: 'Consultation Hour',
    date: format(isValid(job?.job?.appointment) ? job?.job?.appointment : new Date(), 'yyyy-MM-dd'),
    price: job?.labour_rate_inc_gst,
  };
  const taxRate = job?.tax_rate || '10.00';
  const taxMultiplier = Big(taxRate).div(100).plus(1);
  const { id } = useParams();
  const { setServiceSelection, serviceSelection } = BookingStore;

  useQuery(['check-selected-services', id], getJobServiceSelection, {
    enabled: !!id,
    onSuccess: (data) => {
      setServiceSelection(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const handleChangeProductQty = (productId: string, qty: number) => {
    const getRate = (id: string) => {
      switch (id) {
        case Rate.SameDay:
          return Big(job?.job.sameday_service_rate).times(taxMultiplier);
        case Rate.Weekend:
          return Big(job?.job.weekend_service_rate).times(taxMultiplier);
        case Rate.Cancellation:
          return Big(job?.job.cancelled_rate).times(taxMultiplier);
        default:
          return job?.labour_rate_inc_gst;
      }
    };
    setSelectedProducts((prevProducts) =>
      prevProducts?.map((product) => {
        return product.id === productId
          ? { ...product, qty: qty, price: Big(getRate(product.id)).times(qty) }
          : product;
      }),
    );
  };

  const handleAddProduct = (product: JsonDataItem) => {
    setSelectedProducts(
      selectedProducts ? [...selectedProducts.filter((item) => item.id !== product.id), product] : [product],
    );
  };

  const handleRemoveProduct = (productId: number) => {
    const updatedProducts = selectedProducts?.filter((product) => product.id !== productId);
    setSelectedProducts(updatedProducts?.length ? updatedProducts : undefined);
  };

  // update price calculation
  const handleFormChange = () => {
    onSelect(
      selectedProducts?.map((product) => {
        return { ...product, date: form?.getFieldValue(`productDate-${product.id}`) };
      }),
    );
  };

  useEffect(() => {
    selectedProducts && handleFormChange();
  }, [selectedProducts]);

  useEffect(() => {
    // update page after api call
    job &&
      setSelectedProducts([
        {
          id: Rate.SameDay,
          label: 'Same Day Service Fee',
          qty: hasSameDayService ? job?.job.sameday_service_qty : 0,
          price: Big(job?.job.sameday_service_total).times(taxMultiplier), // legacy service prices provided excl gst
        },
        {
          id: Rate.Weekend,
          label: 'Weekend/PH Service Fee',
          qty: hasWeekendService ? job?.job.weekend_service_qty : 0,
          price: Big(job?.job.weekend_service_total).times(taxMultiplier),
        },
        {
          id: Rate.Cancellation,
          label: 'Cancellation Fee',
          qty: hasCancelationFee ? 1 : 0,
          price: hasCancelationFee ? Big(job?.job.cancelled_rate).times(taxMultiplier) : '0.00',
        },
        ...(job?.jobs_labour?.map((consultation: JsonDataItem) => ({
          id: `existing_${Rate.Consultation}_${consultation.ID}`,
          label: 'Consultation Hour',
          qty: consultation.hours,
          price: Big(job?.labour_rate_inc_gst).times(consultation.hours),
          date: consultation.date,
        })) || []),
      ]);
  }, [job]);

  return (
    <Space.$ direction={Space.Direction.Vertical} className={styles.serviceList}>
      {isLegacySource && (
        <Flex align="center">
          <Tag color="red">This job is booked in Core BMS</Tag>
          <Button.$
            type={Button.Type.Primary}
            dataTestId=""
            onClick={() => {
              window.open(`${env.LEGACY_BMS_URL}jobs/index/edit/id/${encryptAesToken(job?.ID.toString())}`, '_blank');
            }}
          >
            Open in Core BMS
          </Button.$>
        </Flex>
      )}
      <Text.$ strong size={Text.Size.LG} className={styles.serviceList__title}>
        Services and Fees
      </Text.$>
      {serviceSelection?.service_attributes?.job_service_type && (
        <Tag color="blue">
          {
            JobServiceTypeLabel[
              serviceSelection?.service_attributes?.job_service_type as keyof typeof JobServiceTypeLabel
            ]
          }
        </Tag>
      )}
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
        <Form.Item name="legacy_products" required rules={[{ required: true }]}>
          <div>
            {selectedProducts?.map((selectedProduct) => (
              <div className={styles.serviceList__item} key={selectedProduct.id}>
                <InputText.$
                  noMargin
                  name={selectedProduct.id}
                  disabled
                  initialValue={selectedProduct.label}
                  width={260}
                />
                {selectedProduct?.id && (
                  <>
                    <InputNumber
                      disabled={selectedProduct.id === Rate.Cancellation || selectedProduct.id.includes('existing')}
                      value={selectedProduct.qty}
                      min={selectedProduct?.date ? 0.5 : 0}
                      max={!selectedProduct?.date ? 1 : undefined}
                      step={selectedProduct?.date ? 0.5 : 1}
                      width={55}
                      className={cx(styles.serviceList__qty, isRequired && styles.serviceList__qty_required)}
                      onChange={(qty) => handleChangeProductQty(selectedProduct.id, qty)}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                    {selectedProduct?.date && (
                      <DatePicker.$
                        disabled={selectedProduct.id.includes('existing')}
                        initialValue={
                          selectedProduct.date
                            ? (parse(selectedProduct.date, 'yyyy-MM-dd', new Date()) as any)
                            : new Date()
                        }
                        name={`productDate-${selectedProduct.id}`}
                        allowClear={false}
                        className={styles.serviceList__datepicker}
                        onSelect={handleFormChange}
                      />
                    )}
                    <div className={styles.serviceList__price}>
                      {selectedProduct.price && (
                        <Text.$>
                          {' $'}
                          {Big(selectedProduct.price || '0').toFixed(2)}
                          <small> inc GST</small>
                        </Text.$>
                      )}
                      {selectedProduct?.date && (
                        <Space.$ justify={Space.Justify.End}>
                          <Button.$
                            dataTestId="remove-product"
                            size={Button.Size.Small}
                            type={Button.Type.Link}
                            className={styles.serviceList__link}
                            onClick={() => {
                              handleRemoveProduct(selectedProduct.id);
                            }}
                          >
                            Remove
                          </Button.$>
                        </Space.$>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}

            <Button.$
              type={Button.Type.Dashed}
              block
              dataTestId="add-new-product"
              className={styles.serviceList__action}
              onClick={() =>
                handleAddProduct({
                  ...newConsultation,
                  id: `new_${Rate.Consultation}_${format(new Date(), 'yyyyMMddHHmmss')}`,
                })
              }
              style={{ marginTop: 8 }}
            >
              + Add New
            </Button.$>
          </div>
        </Form.Item>
      </Space.$>

      {selectedProducts && selectedProducts.filter((prod) => prod.id).length > 0 && (
        <>
          <Divider />
          <div className={styles.serviceList__total}>
            <Text.$ size={Text.Size.SM}>
              Total <br />
              <b>{selectedProducts.filter((prod) => prod.id).length}</b> items
            </Text.$>
            <Text.$ strong>
              ${totalPrice} <small>inc GST</small>
            </Text.$>
          </div>
        </>
      )}
    </Space.$>
  );
});
